/**
 * Servicio para el endpoint /recogidas
 *
 * Integración con API de recogidas (MSoft)
 * Ver documentación completa: ../../../api/docs/api/recogidas.md
 *
 * Endpoints:
 * - GET /recogidas - Lista recogidas con filtros opcionales
 * - GET /recogidas/:id - Obtiene una recogida por ID
 * - POST /recogidas - Crea nueva recogida (se sincroniza con MSoft)
 * - PUT /recogidas/:id - Actualiza recogida (solo SOLICITADA)
 * - DELETE /recogidas/:id - Elimina recogida (solo SOLICITADA)
 */

import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";

/**
 * Servicio para gestión de recogidas
 */
class RecogidasService {
    /**
     * GET /recogidas - Lista recogidas con filtros opcionales
     *
     * @param {Object} params - Parámetros de filtrado
     * @param {number} params.usuarioId - Filtrar por ID de usuario
     * @param {string} params.estado - Estado (SOLICITADA, CONFIRMADA, ERROR, ASIGNADA,
     *   EN_CURSO, REALIZADA, ANULADA)
     * @param {string} params.desde - Fecha inicio (ISO8601). Filtra por fecha de creación
     * @param {string} params.hasta - Fecha fin (ISO8601). Filtra por fecha de creación
     * @returns {Promise<Array>} Lista de recogidas
     */
    static async list(params = {}) {
        // Filtrar parámetros vacíos (la API rechaza strings vacíos)
        const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});

        return axios
            .get(HostService.getHost() + 'recogidas', {
                params: filteredParams,
                ...AuthService.setToken(),
            })
            .then((res) => {
                // La API retorna { data: [...recogidas], message: "..." }
                return res.data.data || [];
            })
            .catch((error) => {
                console.error('Error al listar recogidas:', error);
                throw new Error(error.response?.data?.error?.message || 'Error al listar recogidas');
            });
    }

    /**
     * POST /recogidas - Crea una nueva recogida
     *
     * La recogida se guarda localmente y se sincroniza con MSoft.
     * Estados posibles en el response:
     * - SOLICITADA: Pendiente confirmación MSoft
     * - CONFIRMADA: MSoft confirmó (codError = 0)
     * - ERROR: MSoft rechazó (codError ≠ 0)
     *
     * @param {Object} payload - Datos de la recogida
     * @param {string} payload.numeroTicket - Número de ticket (requerido)
     * @param {string} payload.clienteCodigo - Código del cliente (requerido)
     * @param {string} payload.clienteNombre - Nombre del cliente (requerido)
     * @param {number|null} payload.direccionLinea - Línea de dirección (null si es personalizada)
     * @param {Object} payload.direccion - Datos de la dirección (requerido)
     * @param {string} payload.direccion.nombre - Nombre de contacto (requerido)
     * @param {string} payload.direccion.direccion - Dirección (requerido)
     * @param {string} payload.direccion.codigoPostal - Código postal 5 dígitos (requerido)
     * @param {string} payload.direccion.poblacion - Población (requerido)
     * @param {string} payload.direccion.provincia - Provincia (requerido)
     * @param {string} payload.observaciones - Observaciones (opcional)
     * @returns {Promise<Object>} Recogida creada. OJO: responde 201 aunque MSoft la
     *   rechace; hay que comprobar `estado === 'ERROR'` y `descripcionErrorMsoft`
     */
    static async create(payload) {
        return axios
            .post(HostService.getHost() + 'recogidas', payload, AuthService.setToken())
            .then((res) => {
                // La API retorna { data: {...recogida}, message: "..." }
                return res.data.data;
            })
            .catch((error) => {
                console.error('Error al crear recogida:', error);
                const errorMessage = error.response?.data?.error?.message ||
                                   error.response?.data?.error?.details?.[0]?.message ||
                                   'Error al crear recogida';
                throw new Error(errorMessage);
            });
    }

    /**
     * DELETE /recogidas/:id - Elimina una recogida
     *
     * Solo se pueden eliminar recogidas en estado SOLICITADA
     * (no confirmadas ni en error, para mantener trazabilidad)
     *
     * @param {number} id - ID de la recogida
     * @returns {Promise<Object>} Mensaje de confirmación
     */
    static async delete(id) {
        return axios
            .delete(HostService.getHost() + `recogidas/${id}`, AuthService.setToken())
            .then((res) => {
                // La API retorna { message: "..." }
                return res.data;
            })
            .catch((error) => {
                console.error('Error al eliminar recogida:', error);
                throw new Error(error.response?.data?.error?.message || 'Error al eliminar recogida');
            });
    }

    /**
     * POST /recogidas/:id/sincronizar - Consulta MSoft y actualiza el seguimiento
     *
     * Vía bajo demanda: misma consulta que el proceso periódico pero inmediata y para
     * una sola recogida. Solo admiten sincronización los estados CONFIRMADA, ASIGNADA
     * y EN_CURSO; el resto responde 400 sin llegar a llamar a MSoft.
     *
     * @param {number} id - ID de la recogida
     * @returns {Promise<Object>} Recogida con el seguimiento actualizado. Si MSoft no
     *   conoce la recogida responde 200 sin cambiar el estado, registrando solo el
     *   intento; lo mismo si la situación devuelta no se puede traducir a un estado
     */
    /**
     * POST /recogidas/:id/cancelar - Cancela la recogida y la saca del ciclo de sincronización
     *
     * Solo desde SOLICITADA, CONFIRMADA, ERROR, ASIGNADA o EN_CURSO; el resto responde 400.
     * OJO: NO da de baja la recogida en MSoft, es un estado interno.
     *
     * @param {number} id - ID de la recogida
     * @returns {Promise<Object>} Recogida en estado CANCELADA
     */
    static async cancelar(id) {
        return axios
            .post(HostService.getHost() + `recogidas/${id}/cancelar`, {}, AuthService.setToken())
            .then((res) => {
                // La API retorna { data: {...recogida}, message: "..." }
                return res.data.data;
            })
            .catch((error) => {
                console.error('Error al cancelar recogida:', error);
                throw new Error(error.response?.data?.error?.message || 'Error al cancelar recogida');
            });
    }

    /**
     * POST /recogidas/:id/recepcionar - Marca la recogida como recibida en almacén
     *
     * Solo desde REALIZADA; el resto responde 400.
     *
     * @param {number} id - ID de la recogida
     * @returns {Promise<Object>} Recogida en estado RECEPCIONADA
     */
    static async recepcionar(id) {
        return axios
            .post(HostService.getHost() + `recogidas/${id}/recepcionar`, {}, AuthService.setToken())
            .then((res) => {
                // La API retorna { data: {...recogida}, message: "..." }
                return res.data.data;
            })
            .catch((error) => {
                console.error('Error al recepcionar recogida:', error);
                throw new Error(error.response?.data?.error?.message || 'Error al recepcionar recogida');
            });
    }

    static async sincronizar(id) {
        return axios
            .post(HostService.getHost() + `recogidas/${id}/sincronizar`, {}, AuthService.setToken())
            .then((res) => {
                // La API retorna { data: {...recogida}, message: "..." }
                return res.data.data;
            })
            .catch((error) => {
                console.error('Error al sincronizar recogida:', error);
                throw new Error(error.response?.data?.error?.message || 'Error al sincronizar recogida');
            });
    }
}

export default RecogidasService;
